import { useMemo } from 'react'
import { Helmet } from 'react-helmet-async'

import { LabFeatureFlags } from 'constants/lab'
import { useAppSelector } from 'hooks/reduxHooks'
import { buildPublicOffersAbsoluteUrl, buildPublicOffersUrl } from 'api/offer'
import { isOfferDisplayFlavorPayload } from 'lib/lab/dynamicPayload'
import { getOfferDeepLink } from 'selectors/offerDeepLinkSelector'
import { getDefaultAirportCode } from 'selectors/flightsSelectors'

interface Props {
  ymalListKey: string
  /**
   * Number of YMAL offer IDs included in the head warm-up URL.
   *
   * MUST equal the number of YMAL tiles that fire `useOffer` immediately on
   * hydration (i.e. tiles rendered with `lazyLoad='none'`). Today that is
   * `YMAL_PRIORITY_TILE_COUNT` (4) in `MetaSearchPage.tsx`; tiles beyond that
   * use `lazyLoad='offer-and-inview'` so they don't dispatch until scrolled
   * near. Those eager dispatches all hit the 32ms `getAccumulatedOfferById`
   * accumulator and batch into a single bulk fetch like
   * `?offerIds=id1,id2,id3,id4`.
   */
  maxOfferIds?: number
}

/**
 * Emits an inline head script that warms the YMAL bulk
 * `/api/v2/public-offers?offerIds=...` request and stores the pending promise
 * on `window.__META_SEARCH_PUBLIC_OFFERS_WARMUP__`.
 *
 * Runtime `getOffersById` reads this map and reuses the same promise/data, so
 * this path does not rely on strict browser cache matching.
 *
 * Correctness invariant: the warm-up URL MUST stay byte-identical to the
 * runtime URL shape. This is achieved by:
 *
 * 1. Reusing the shared `buildPublicOffersUrl` helper (also called by
 *    `getOffersById`).
 * 2. Reusing `buildPublicOffersAbsoluteUrl` to mirror the endpoint + `brand`
 *    augmentation that runtime fetches receive through `brandLoader`.
 * 3. Reading the same per-request selectors (`flightOrigin`, `offerDeepLink`,
 *    lab `flavor` payload) that `fetchAccumulatedOfferById` reads at fetch
 *    time.
 */
function MetaSearchPageWarmupHints({ ymalListKey, maxOfferIds = 4 }: Props) {
  const region = useAppSelector((state) => state.geo.currentRegionCode)
  const flightOrigin = useAppSelector(getDefaultAirportCode)
  const offerDeepLink = useAppSelector(getOfferDeepLink)
  const isSpoofed = useAppSelector((state) => state.auth.account.isSpoofed)
  const offerDisplayFlavorFlag = useAppSelector(
    (state) => state.system.features.labFlags[LabFeatureFlags.enableOfferDisplayFlavor],
  )
  const ymalOfferIds = useAppSelector(
    (state) => state.offer.offerLists[ymalListKey]?.offerIds,
  )

  const flavor = offerDisplayFlavorFlag?.enabled && isOfferDisplayFlavorPayload(offerDisplayFlavorFlag.value)
    ? offerDisplayFlavorFlag.value.flavor
    : undefined

  const warmupUrl = useMemo(() => {
    if (!ymalOfferIds || ymalOfferIds.length === 0) return undefined
    if (offerDeepLink || isSpoofed) return undefined

    const path = buildPublicOffersUrl(ymalOfferIds.slice(0, maxOfferIds), {
      region,
      flightOrigin,
      offerDeepLink: undefined,
      flavor,
    })

    return buildPublicOffersAbsoluteUrl(path)
  }, [ymalOfferIds, region, flightOrigin, flavor, offerDeepLink, isSpoofed, maxOfferIds])

  const warmupScript = useMemo(() => {
    if (!warmupUrl) return undefined
    const serialisedUrl = JSON.stringify(warmupUrl)
    return `
      (function() {
        window.__META_SEARCH_PUBLIC_OFFERS_WARMUP__ = window.__META_SEARCH_PUBLIC_OFFERS_WARMUP__ || {};
        if (window.__META_SEARCH_PUBLIC_OFFERS_WARMUP__[${serialisedUrl}]) return;
        window.__META_SEARCH_PUBLIC_OFFERS_WARMUP__[${serialisedUrl}] = fetch(${serialisedUrl}, { credentials: 'same-origin' })
          .then(function(response) {
            if (!response.ok) return undefined;
            return response.json();
          })
          .catch(function() {
            return undefined;
          });
      })();
    `
  }, [warmupUrl])

  if (!warmupScript) return null

  return (
    <Helmet>
      <script>{warmupScript}</script>
    </Helmet>
  )
}

export default MetaSearchPageWarmupHints
